import { createRoot } from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import { activateConsoleFilter, installLovableNoiseFilter } from './utils/sandboxConsoleFilter'
import { logger } from './utils/logger'
import { MemoryManager } from './utils/memoryManager'
import { PerformanceOptimizer } from './utils/performanceOptimizer'
import { ErrorBoundary } from './components/common/ErrorBoundary'
import { updateActivityTimestamp } from './utils/lazyRetry'

// Install minimal noise filter early (dev + iframe only)
try {
  installLovableNoiseFilter();
} catch (error) {
  console.warn('Failed to install noise filter:', error);
}

// Determine environment
const hostname = window.location.hostname;
const isLovableEditor = hostname.includes('lovable.dev') || 
                       hostname.includes('lovableproject.com') ||
                       hostname.includes('lovable.app');
const isProduction = import.meta.env.PROD;

// Reduce console noise in production unless debug is enabled
if (isProduction) {
  const urlParams = new URLSearchParams(window.location.search);
  const debugEnabled = urlParams.get('log') === 'debug' || localStorage.getItem('LOG_LEVEL') === 'debug';
  
  if (!debugEnabled) {
    // Mute noisy console methods in production
    const originalLog = console.log;
    const originalInfo = console.info;
    const originalDebug = console.debug;
    
    console.log = () => {}; // Silent in production
    console.info = () => {}; // Silent in production  
    console.debug = () => {}; // Silent in production
    
    // Keep warn and error for actual issues
    logger.info('🔇 Production mode: console.log/info/debug silenced. Use ?log=debug to enable.');
  }
}

// Activate console filter AFTER production overrides to ensure it works
activateConsoleFilter();

// Initialize performance optimizations for weak devices
const memoryManager = MemoryManager.getInstance();
const performanceOptimizer = PerformanceOptimizer.getInstance();

memoryManager.initializeMemoryMonitoring();

// Log performance configuration
logger.info('📱 Performance config:', performanceOptimizer.getConfig());

// Initialize activity tracking
updateActivityTimestamp();

// Add error handling for unhandled promise rejections (module loading errors)
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason?.message?.includes('Loading chunk') || 
      event.reason?.message?.includes('Failed to fetch') ||
      event.reason?.message?.includes('MIME type')) {
    console.warn('Module loading error detected:', event.reason);
    logger.warn('Cache invalidation may be needed - user should reload');
    event.preventDefault(); // Prevent default error handling
  }
});

createRoot(document.getElementById("root")!).render(
  <ErrorBoundary>
    <App />
  </ErrorBoundary>
);
